import {
	Box,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormHelperText,
} from "@mui/material";
import {
	Contact,
	EmployeeWorkingConditionInformation,
	FederalState,
	useGetAllFederalStatesLazyQuery,
	useGetAllWorkingTimeModelsLazyQuery,
	WorkingTimeModel,
} from "@repo/server-api";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, {
	useState,
	useEffect,
	forwardRef,
	useRef,
	useCallback,
	useImperativeHandle,
	useMemo,
} from "react";
import * as Yup from "yup";

import DateField from "../fields/DateField";

const WorkingConditions = forwardRef(
	(
		props: Readonly<{
			contact: Contact | null;
			setContact: (value: Contact | null) => void;
			errors?: Record<string, boolean | object | string>;
			setErrors?: (value: Record<string, boolean | object | string>) => void;
			info?: EmployeeWorkingConditionInformation;
			setInfo?: (value: EmployeeWorkingConditionInformation) => void;
		}>,
		ref: React.Ref<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		}>,
	) => {
		const { t } = useTranslation();

		const appliesFromRef = useRef<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		} | null>(null);

		const [workingTimeModels, setWorkingTimeModels] = useState<
			WorkingTimeModel[]
		>([]);

		useEffect(() => {
			getWorkingTimeModels({
				variables: {
					nameContains: "",
					onlyVisible: false,
					showDeleted: true,
					first: 100,
					after: "",
				},
			});
			getFederalLocations();
			 
		}, []);

		const [federalLocations, setFederalLocations] = useState<FederalState[]>(
			[],
		);

		const [getFederalLocations] = useGetAllFederalStatesLazyQuery({
			fetchPolicy: "no-cache",
			onCompleted(data) {
				if (data && data.allFederalStates) {
					const tmp = data.allFederalStates.map((node) => ({
						...node,
					}));
					setFederalLocations(tmp);
				}
			},
		});

		const [getWorkingTimeModels] = useGetAllWorkingTimeModelsLazyQuery({
			fetchPolicy: "no-cache",
			onCompleted(data) {
				if (data && data.allWorkingTimeModels?.nodes) {
					const tmp = data.allWorkingTimeModels.nodes.map((node) => ({
						...(node as unknown as WorkingTimeModel),
					}));
					setWorkingTimeModels(tmp);
				}
			},
		});

		const validationSchema = Yup.object().shape({
			workingTimeModelId: Yup.string().required(
				t("validation.workingTimeModel.required"),
			),
			federalLocation: Yup.string().required(
				t("validation.federalLocation.required"),
			),
		});

		const validateFields = useCallback(async () => {
			const localErrors: Record<string, boolean | object | string> = {};

			try {
				await validationSchema.validate(info, { abortEarly: false });
			} catch (err) {
				if (err instanceof Yup.ValidationError) {
					err.inner.forEach((error) => {
						if (error.path) {
							localErrors[error.path] = error.message;
						}
					});
				}
			}
			localErrors.appliesFrom =
				(await appliesFromRef.current?.validate()) || "";

			props.setErrors?.(localErrors);

			return localErrors;
			 
		}, [props]);

		useImperativeHandle(ref, () => ({
			validate: validateFields,
		}));

		const info = useMemo(() => {
			if (props.info) {
				return props.info;
			}
			return (
				props.contact?.additionalInformation.filter(
					(info): info is EmployeeWorkingConditionInformation =>
						(info as EmployeeWorkingConditionInformation).__typename ===
						"EmployeeWorkingConditionInformation",
				)[0] ?? {
					id: "",
					__typename: "EmployeeWorkingConditionInformation",
					to: "",
					from: "",
					workingTimeModelId: "",
					federalLocation: "",
				}
			);
		}, [props.contact, props.info]);

		return (
			<div>
				<Box className="w-100">
					<DateField
						isLoaded={true}
						ref={appliesFromRef}
						value={info.from ?? ""}
						setValue={(value) => {
							if (value && value !== "Invalid Date") {
								if (props.info) {
									props.setInfo?.({
										...props.info,
										from: dayjs(value, "YYYY-MM-DD").format("YYYY-MM-DD"),
									});
									props.setErrors?.({
										...props.errors,
										appliesFrom: "",
									});
									return;
								}
								const additionalInformation =
									props.contact?.additionalInformation;
								const workingConditionInfo: EmployeeWorkingConditionInformation =
									additionalInformation?.filter(
										(i): i is EmployeeWorkingConditionInformation =>
											(i as EmployeeWorkingConditionInformation).__typename ===
											"EmployeeWorkingConditionInformation",
									)[0] ?? {
										id: "",
										__typename: "EmployeeWorkingConditionInformation",
										to: "",
										from: "",
										workingTimeModelId: "",
										federalLocation: "",
									};
								props.setContact?.({
									...props.contact!,
									additionalInformation: [
										...(additionalInformation ?? []).filter(
											(i) =>
												(i as EmployeeWorkingConditionInformation)
													.__typename !== "EmployeeWorkingConditionInformation",
										),
										{
											...workingConditionInfo,
											from: dayjs(value, "YYYY-MM-DD").format("YYYY-MM-DD"),
										} as EmployeeWorkingConditionInformation,
									],
								});
								props.setErrors?.({
									...props.errors,
									appliesFrom: "",
								});
							}
						}}
						label={t("from")}
						errors={
							props.errors?.appliesFrom as Record<
								string,
								boolean | object | string
							>
						}
						setErrors={(value) =>
							props.setErrors?.({ ...props.errors, appliesFrom: value })
						}
					/>
				</Box>
				<Box className="w-100" sx={{ mt: 3 }}>
					<DateField
						isLoaded={true}
						value={info.to ?? ""}
						setValue={(value) => {
							if (value && value !== "Invalid Date") {
								if (props.info) {
									props.setInfo?.({
										...props.info,
										to: dayjs(value, "YYYY-MM-DD").format("YYYY-MM-DD"),
									});
									return;
								}
								const additionalInformation =
									props.contact?.additionalInformation;
								const workingConditionInfo: EmployeeWorkingConditionInformation =
									additionalInformation?.filter(
										(i): i is EmployeeWorkingConditionInformation =>
											(i as EmployeeWorkingConditionInformation).__typename ===
											"EmployeeWorkingConditionInformation",
									)[0] ?? {
										id: "",
										__typename: "EmployeeWorkingConditionInformation",
										to: "",
										from: "",
										workingTimeModelId: "",
										federalLocation: "",
									};
								props.setContact?.({
									...props.contact!,
									additionalInformation: [
										...(additionalInformation ?? []).filter(
											(i) =>
												(i as EmployeeWorkingConditionInformation)
													.__typename !== "EmployeeWorkingConditionInformation",
										),
										{
											...workingConditionInfo,
											to: dayjs(value, "YYYY-MM-DD").format("YYYY-MM-DD"),
										} as EmployeeWorkingConditionInformation,
									],
								});
							}
						}}
						label={t("to")}
					/>
				</Box>
				<Box className="w-100">
					<FormControl sx={{ mt: 3 }} fullWidth>
						<InputLabel
							id="workingTimeModelId"
							className={props.errors?.workingTimeModelId ? "red" : ""}
						>
							{t("work-time-model")}
						</InputLabel>
						<Select
							labelId="workingTimeModelId"
							value={info.workingTimeModelId ?? ""}
							label={t("work-time-model")}
							onChange={(event) => {
								if (props.info) {
									props.setInfo?.({
										...props.info,
										workingTimeModelId: event.target.value ?? "",
									});
									props.setErrors?.({
										...props.errors,
										workingTimeModelId: "",
									});
									return;
								}
								const additionalInformation =
									props.contact?.additionalInformation;
								const workingConditionInfo: EmployeeWorkingConditionInformation =
									additionalInformation?.filter(
										(i): i is EmployeeWorkingConditionInformation =>
											(i as EmployeeWorkingConditionInformation).__typename ===
											"EmployeeWorkingConditionInformation",
									)[0] ?? {
										id: "",
										__typename: "EmployeeWorkingConditionInformation",
										to: "",
										from: "",
										workingTimeModelId: "",
										federalLocation: "",
									};
								props.setContact?.({
									...props.contact!,
									additionalInformation: [
										...(additionalInformation ?? []).filter(
											(i) =>
												(i as EmployeeWorkingConditionInformation)
													.__typename !== "EmployeeWorkingConditionInformation",
										),
										{
											...workingConditionInfo,
											workingTimeModelId: event.target.value ?? "",
										} as EmployeeWorkingConditionInformation,
									],
								});
								props.setErrors?.({ ...props.errors, workingTimeModelId: "" });
							}}
							error={!!props.errors?.workingTimeModelId}
						>
							{workingTimeModels?.map(
								(entry: WorkingTimeModel, index: number) => {
									return (
										<MenuItem
											key={"entry" + index}
											value={entry.id?.toString()}
										>
											{entry.name}
										</MenuItem>
									);
								},
							)}
						</Select>
						{props.errors?.workingTimeModelId && (
							<FormHelperText className="red">
								{" "}
								{props.errors.workingTimeModelId as string}
							</FormHelperText>
						)}
					</FormControl>
				</Box>
				<Box className="w-100">
					<FormControl sx={{ mt: 3 }} fullWidth>
						<InputLabel
							id="federalLocation"
							className={props.errors?.federalLocation ? "red" : ""}
						>
							{t("federal-location")}
						</InputLabel>
						<Select
							labelId="federalLocation"
							value={info.federalLocation ?? ""}
							label={t("federal-location")}
							onChange={(event) => {
								if (props.info) {
									props.setInfo?.({
										...props.info,
										federalLocation: event.target.value ?? "",
									});
									props.setErrors?.({
										...props.errors,
										federalLocation: "",
									});
									return;
								}
								const additionalInformation =
									props.contact?.additionalInformation;
								const workingConditionInfo: EmployeeWorkingConditionInformation =
									additionalInformation?.filter(
										(i): i is EmployeeWorkingConditionInformation =>
											(i as EmployeeWorkingConditionInformation).__typename ===
											"EmployeeWorkingConditionInformation",
									)[0] ?? {
										id: "",
										__typename: "EmployeeWorkingConditionInformation",
										to: "",
										from: "",
										workingTimeModelId: "",
										federalLocation: "",
									};
								props.setContact?.({
									...props.contact!,
									additionalInformation: [
										...(additionalInformation ?? []).filter(
											(i) =>
												(i as EmployeeWorkingConditionInformation)
													.__typename !== "EmployeeWorkingConditionInformation",
										),
										{
											...workingConditionInfo,
											federalLocation: event.target.value ?? "",
										} as EmployeeWorkingConditionInformation,
									],
								});
								props.setErrors?.({ ...props.errors, federalLocation: "" });
							}}
							error={!!props.errors?.federalLocation}
						>
							{federalLocations?.map((entry: FederalState, index: number) => {
								return (
									<MenuItem
										key={"entry" + index}
										value={entry.code?.toString()}
									>
										{entry.name}
									</MenuItem>
								);
							})}
						</Select>
						{props.errors?.federalLocation && (
							<FormHelperText className="red">
								{" "}
								{props.errors.federalLocation as string}
							</FormHelperText>
						)}
					</FormControl>
				</Box>
			</div>
		);
	},
);

WorkingConditions.displayName = "WorkingConditions";
export default WorkingConditions;
